import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUser, FaPlus, FaEdit, FaTrash, FaLock, FaEnvelope, FaKey } from 'react-icons/fa';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'agent',
    phone: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-generate username from first initial + last name when creating new user
      if (!editingUser && (field === 'firstName' || field === 'lastName')) {
        const firstName = field === 'firstName' ? value : prev.firstName;
        const lastName = field === 'lastName' ? value : prev.lastName;

        if (firstName && lastName) {
          const username = (firstName.charAt(0) + lastName).toLowerCase().replace(/[^a-z0-9]/g, '');
          updated.username = username;
        }
      }

      return updated;
    });
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'agent',
      phone: ''
    });
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/users', formData);
      if (response.data.success) {
        alert('User created successfully!');
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '', // Don't pre-fill password
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      phone: user.phone || ''
    });
    setShowCreateForm(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        phone: formData.phone
      };

      // Only include password if it was changed
      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await axios.put(`/users/${editingUser.id}`, updateData);
      if (response.data.success) {
        alert('User updated successfully!');
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`)) {
      return;
    }

    try {
      await axios.put(`/users/${userId}`, { isActive: !currentStatus });
      alert('User status updated');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user status');
    }
  };

  const handleSendWelcomeEmail = async (userId, userEmail) => {
    if (!window.confirm(`Send welcome email to ${userEmail}?`)) {
      return;
    }

    try {
      const response = await axios.post(`/users/${userId}/send-welcome-email`);
      if (response.data.success) {
        alert('Welcome email sent successfully!');
      }
    } catch (error) {
      console.error('Error sending welcome email:', error);
      // Silent fail - email service may not be configured yet
      alert('Email service is currently unavailable. User account is active but welcome email was not sent.');
    }
  };

  const handleSendPasswordResetEmail = async (userId, userEmail) => {
    if (!window.confirm(`Send password reset email to ${userEmail}?`)) {
      return;
    }

    try {
      const response = await axios.post(`/users/${userId}/send-password-reset-email`);
      if (response.data.success) {
        alert('Password reset email sent successfully!');
      }
    } catch (error) {
      console.error('Error sending password reset email:', error);
      alert(error.response?.data?.message || 'Failed to send password reset email');
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'role-admin',
      agent: 'role-agent',
      staff: 'role-staff'
    };
    return classes[role] || 'role-default';
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="user-header">
        <h1><FaUser /> User Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          <FaPlus /> Create New User
        </button>
      </div>

      {showCreateForm && (
        <div className="modal-overlay" onClick={() => resetForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
              <button className="close-btn" onClick={() => resetForm()}>&times;</button>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Username {editingUser ? '(cannot be changed)' : '(auto-generated)'}</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    required
                    disabled={true}
                    placeholder={editingUser ? "" : "Will be generated from name"}
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                  {!editingUser && <small>Format: First initial + Last name (e.g., Patrick King → pking)</small>}
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    required
                  >
                    <option value="agent">Agent</option>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{editingUser ? 'New Password (optional)' : 'Temporary Password *'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder={editingUser ? "Leave blank to keep current password" : "User should change this on first login"}
                  required={!editingUser}
                  minLength="6"
                />
                <small>{editingUser ? 'Minimum 6 characters. Leave blank to keep current password.' : 'Minimum 6 characters. User will be prompted to change on first login.'}</small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => resetForm()}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">No users found</td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id} className={!user.is_active ? 'inactive' : ''}>
                  <td className="username-cell">{user.username}</td>
                  <td>{user.first_name} {user.last_name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</td>
                  <td className="actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEditUser(user)}
                      title="Edit User"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleSendWelcomeEmail(user.id, user.email)}
                      title="Send Welcome Email"
                      style={{ color: '#007bff' }}
                    >
                      <FaEnvelope />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleSendPasswordResetEmail(user.id, user.email)}
                      title="Send Password Reset Email"
                      style={{ color: '#17a2b8' }}
                    >
                      <FaKey />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleToggleActive(user.id, user.is_active)}
                      title={user.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <FaLock />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="security-notes">
        <h3>Security Best Practices</h3>
        <ul>
          <li>Only create accounts for authorized staff members</li>
          <li>Use strong temporary passwords (will be changed on first login)</li>
          <li><strong>Agent Role:</strong> Can create bonds, manage defendants, handle payments</li>
          <li><strong>Staff Role:</strong> Can view data but cannot create new bonds</li>
          <li><strong>Admin Role:</strong> Full system access including user management</li>
          <li>Deactivate accounts immediately when staff leaves</li>
          <li>Review user access regularly</li>
        </ul>
      </div>
    </div>
  );
};

export default UserManagement;
